<template>
    <v-form ref="sample-item-form" v-model="formValid" @input="handleFormInput">
        <confirm ref="confirm"></confirm>
        <div>
            <span><b>{{sample.title}}</b></span>
            <span class="control-buttons">
                <v-btn icon text color="primary" small class="ma-0 pa-0"
                       :disabled="isMoveUpButtonActive"
                       @click="$emit('move-up', sample.id)">
                    <v-icon small>fas fa-long-arrow-alt-down</v-icon>
                </v-btn>
                <v-btn icon text color="primary" small class="ma-0 pa-0"
                       :disabled="isMoveDownButtonActive"
                       @click="$emit('move-down', sample.id)">
                    <v-icon small>fas fa-long-arrow-alt-up</v-icon>
                </v-btn>
                <v-btn icon text small class="ml-3 pa-0"
                       :disabled="readonly"
                       @click="handleDeleteButtonClick">
                    <v-icon small>fas fa-trash</v-icon>
                </v-btn>
            </span>
        </div>
        <div class="cleaner"></div>
        <div v-bind:class="inputClasses">
            <v-textarea
                    ref="karyotype-selector"
                    label="Karyotype"
                    :disabled="readonly"
                    v-model="karyotype"
                    :rules="calcKaryotypeRules()"
                    rows="2"
                    auto-grow
            >
                <v-btn v-if="isKaryotypeForceButtonVisible()" slot="append" small text icon color="error"
                       @click="handleClickKaryotypeForceButton">
                    <v-icon small>fas fa-fist-raised</v-icon>
                </v-btn>
            </v-textarea>
            <v-textarea
                    label="Interpretation"
                    ref="interpretation-selector"
                    :disabled="readonly"
                    :rules="calcInterpretationRules()"
                    rows="2"
                    auto-grow
                    v-model="interpretation"
                    :append-icon="interpretationOptionsVisible ? 'fas fa-caret-up' : 'fas fa-caret-down'"
                    @click:append="handleInterpretationSelectorClick">
                <v-btn v-if="isInterpretationForceButtonVisible()" slot="append" small text icon color="error"
                       @click="handleClickInterpretationForceButton">
                    <v-icon small>fas fa-fist-raised</v-icon>
                </v-btn>
            </v-textarea>
            <v-menu v-model="interpretationOptionsVisible"
                    max-height="500"
                    :position-x="interpretationMenuX"
                    :position-y="interpretationMenuY">
                <v-list>
                    <v-list-item
                            v-for="interpretationOption in reportSampleTemplatesPrepared"
                            :key="interpretationOption.id"
                            @click="handleSelectInterpretationTemplate(interpretationOption)"
                    >
                        <v-list-item-content class="interpretation-item-wrapper">
                            <v-list-item-title v-text="interpretationOption.interpretationTitle"
                                               v-bind:class="interpretationOption.cssClasses"></v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-textarea
                    label="Recommendations"
                    :disabled="readonly"
                    :rules="recommendationsRules"
                    item-text="text"
                    v-model="recommendations"
                    :return-object="true"
                    rows="2"
                    auto-grow
                    :append-icon="recommendationOptionsVisible ? 'fas fa-caret-up' : 'fas fa-caret-down'"
                    @click:append="handleRecommendationsSelectorClick"
            ></v-textarea>
            <v-menu v-model="recommendationOptionsVisible"
                    max-height="500"
                    :position-x="recommendationsMenuX"
                    :position-y="recommendationsMenuY">
                <v-list>
                    <v-list-item
                            v-for="recommendationOption in recommendationOptions"
                            :key="recommendationOption.text"
                            @click="setRecommendations(recommendationOption)"
                    >
                        <v-list-item-content>
                            <v-list-item-title v-text="recommendationOption.text"></v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>
    </v-form>

</template>

<script>
    import _ from 'lodash'
    import {mapGetters, mapState} from 'vuex'
    import Confirm from '../Common/Confirm'
    import {KaryotypeRegex} from '../../dictionaries/common-rules'

    const baseKaryotypeRules = [
        v => !!v || 'Please, enter karyotype',
        function (v) {
            return KaryotypeRegex.test(v) || 'Wrong symbols'
        },
    ]

    const baseInterpretationRules = [
        v => !!v || 'Please, enter interpretation'
    ]

    export default {
        name: "SampleItem",
        props: {
            sample: Object,
            readonly: Boolean,
            canMoveUp: Boolean,
            canMoveDown: Boolean,
        },
        components: {
            Confirm
        },
        data() {
            return {
                interpretationOptionsVisible: false,
                interpretationMenuX: null,
                interpretationMenuY: null,
                recommendationOptionsVisible: false,
                recommendationsMenuX: null,
                recommendationsMenuY: null,
                formValid: false,
                karyotypeLastValidValue: this.sample.karyotype,
                interpretationLastValidValue: this.sample.interpretation,
                lastSelectedRecommendationType: null,
                //karyotypeRules: baseKaryotypeRules,
                recommendationsRules: [
                    v => !!v || 'Enter recommendations'
                ],
                recommendationOptions: [
                    {
                        type: 'recommended',
                        text: 'Рекомендован',
                    },
                    {
                        type: 'notRecommended',
                        text: 'Не рекомендован'
                    },
                    {
                        type: 'other',
                        text: 'Рекомендован с информированного согласия пациента'
                    },
                    {
                        type: 'other',
                        text: 'Рекомендован с информированного согласия пациента после очной консультации врача-генетика'
                    },
                    {
                        type: 'other',
                        text: 'Рекомендована повторная биопсия'
                    },
                ]
            }
        },
        computed: {
            ...mapState('pgtCases', {
                reportSampleTemplates: 'reportSampleTemplates'
            }),
            ...mapGetters('pgtCases', {
                reportSampleTemplatesById: 'reportSampleTemplatesById'
            }),
            reportSampleTemplatesPrepared() {
                let result = []

                for (let reportSampleTemplate of this.reportSampleTemplates) {
                    result.push({
                        ...reportSampleTemplate,
                        cssClasses: this.getCssClassesByRecommendationType(_.get(reportSampleTemplate, ['recommendations', 'type'], null))
                    })
                }

                return result
            },
            inputClasses() {
                const recommendationType = _.get(this.sample, ['recommendations', 'type'], null)
                return this.getCssClassesByRecommendationType(recommendationType)
            },
            karyotype: {
                get() {
                    return this.sample.karyotype
                },
                set(value) {
                    this.sample.karyotype = value.replace(/\r?\n/g, "\r\n")
                }
            },
            interpretation: {
                get() {
                    return this.sample.interpretation
                },
                set(value) {
                    this.sample.interpretation = value.replace(/\r?\n/g, "\r\n")
                }
            },
            recommendations: {
                get() {
                    return _.get(this.sample, ['recommendations', 'text'], null)
                },
                set(value) {
                    this.setRecommendations(value)
                }
            },
            isMoveUpButtonActive() {
                return this.readonly || !this.canMoveUp
            },
            isMoveDownButtonActive() {
                return this.readonly || !this.canMoveDown
            }
        },
        methods: {
            calcKaryotypeRules() {
                const templateId = _.get(this.sample, 'templateId', null)

                if (templateId === null) {
                    return baseKaryotypeRules
                }

                const template = this.reportSampleTemplatesById[templateId]

                if (!template || template.karyotypeRules === null) {
                    return baseKaryotypeRules
                }

                let rules = _.cloneDeep(baseKaryotypeRules)

                const karyotypeRegex = new RegExp(template.karyotypeRules)

                rules.push((v) => {
                    if (this.karyotypeLastValidValue !== null && this.karyotypeLastValidValue === v) {
                        return true
                    }

                    try {
                        const testResult = karyotypeRegex.test(v)

                        if (testResult) {
                            return true
                        }
                    } catch (e) {
                        return 'Incorrect validation rule'
                    }

                    return 'Please, specify karyotype'
                })

                return rules
            },
            calcInterpretationRules() {
                const templateId = _.get(this.sample, 'templateId', null)

                if (templateId === null) {
                    return baseInterpretationRules
                }

                const template = this.reportSampleTemplatesById[templateId]

                if (!template || template.interpretationRules === null) {
                    return baseInterpretationRules
                }

                let rules = _.cloneDeep(baseInterpretationRules)

                const interpretationRegex = new RegExp(template.interpretationRules)

                rules.push((v) => {
                    if (this.interpretationLastValidValue !== null && this.interpretationLastValidValue === v) {
                        return true
                    }

                    try {
                        const testResult = interpretationRegex.test(v)

                        if (testResult) {
                            return true
                        }
                    } catch (e) {
                        return 'Incorrect validation rule'
                    }

                    return 'Please, specify interpretation'
                })

                return rules
            },
            setRecommendations(value) {
                //Если value - это строка, то превращаем в объект
                let newValue = _.clone(value)
                if (_.isObject(value)) {
                    this.lastSelectedRecommendationType = _.get(value, 'type', null)
                }

                if (_.isString(value)) {
                    newValue = {
                        type: this.lastSelectedRecommendationType || 'other',
                        text: newValue.replace(/\r?\n/g, "\r\n")
                    }
                }

                this.sample.recommendations = newValue
            },
            isKaryotypeForceButtonVisible() {
                const reportSampleTemplate = _.get(this.reportSampleTemplatesById, _.get(this.sample, 'templateId', null), null)

                let hasDiffFromTemplate = true
                if (reportSampleTemplate !== null) {
                    hasDiffFromTemplate = reportSampleTemplate.karyotypeTemplate !== this.sample.karyotype
                }

                return hasDiffFromTemplate &&
                    this.sample.karyotype !== null &&
                    this.sample.karyotype.length > 0 &&
                    this.$refs['karyotype-selector'] &&
                    !this.$refs['karyotype-selector'].valid
            },
            isInterpretationForceButtonVisible() {
                const reportSampleTemplate = _.get(this.reportSampleTemplatesById, _.get(this.sample, 'templateId', null), null)

                let hasDiffFromTemplate = true
                if (reportSampleTemplate !== null) {
                    hasDiffFromTemplate = reportSampleTemplate.interpretationTemplate !== this.sample.interpretation
                }

                return hasDiffFromTemplate &&
                    this.sample.interpretation !== null &&
                    this.sample.interpretation.length > 0 &&
                    this.$refs['interpretation-selector'] &&
                    !this.$refs['interpretation-selector'].valid
            },
            handleDeleteButtonClick() {
                this.$emit('delete', _.get(this.sample, 'id'))
            },
            handleFormInput(value) {
                this.$emit('validation-changed', value)
            },
            validate() {
                return this.$refs['sample-item-form'].validate()
            },
            isValid() {
                return this.formValid
            },
            handleInterpretationSelectorClick(e) {
                this.interpretationMenuX = e.clientX
                this.interpretationMenuY = e.clientY
                this.$nextTick(() => {
                    this.interpretationOptionsVisible = true
                })
            },
            handleRecommendationsSelectorClick(e) {
                this.recommendationsMenuX = e.clientX
                this.recommendationsMenuY = e.clientY
                this.$nextTick(() => {
                    this.recommendationOptionsVisible = true
                })
            },
            handleSelectInterpretationTemplate(option) {
                this.sample.interpretation = option.interpretationTemplate
                this.sample.templateId = option.id

                this.recommendations = option.recommendations
                this.karyotype = option.karyotypeTemplate
                //this.$refs['karyotype-selector'].resetValidation()
                //this.$refs['interpretation-selector'].resetValidation()
            },
            getCssClassesByRecommendationType(type) {
                return {
                    'not-recommended': type === 'notRecommended',
                    'recommended': type === 'recommended',
                    'other': type === 'other',
                }
            },
            handleClickKaryotypeForceButton() {
                this.$refs.confirm.open('Karyotype confirmation', `'${this.sample.karyotype}' is not expected value. Do you want to force?`, {width: 600}).then(async (confirm) => {
                    if (!confirm) {
                        return
                    }
                    this.karyotypeLastValidValue = this.sample.karyotype

                    this.$refs['karyotype-selector'].validate()
                })
            },
            handleClickInterpretationForceButton() {
                this.$refs.confirm.open('Interpretation confirmation', `'${this.sample.interpretation}' is not expected value. Do you want to force?`, {width: 600}).then(async (confirm) => {
                    if (!confirm) {
                        return
                    }
                    this.interpretationLastValidValue = this.sample.interpretation

                    this.$refs['interpretation-selector'].validate()
                })
            }
        }
    }
</script>

<style scoped>
    .delete-sample-item-button {
        float: right;
    }

    .control-buttons {
        float: right;
    }
</style>

<style>
    .not-recommended input, .not-recommended textarea, .interpretation-item-wrapper .not-recommended {
        color: #ff5252 !important;
    }

    .other input, .other textarea, .interpretation-item-wrapper .other {
        color: #1976d2 !important;
    }
</style>